import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Upload, Download, Trash2, FileCheck2, FileWarning, Sparkles, FileText, Camera } from "lucide-react";
import { ScanDialog } from "@/components/ScanDialog";
import { EXPIRING, ExpiryDialog } from "@/components/ExpiryDialog";
import { RenewDialog } from "@/components/RenewDialog";
import { RenewalStreak } from "@/components/RenewalStreak";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/AppLayout";
import { ReadinessMeter } from "@/components/ReadinessMeter";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr, usePoll, startCheckout } from "@/lib/api";
import { CheckoutDisclosure } from "@/components/FundingDisclosure";
import { toast } from "sonner";
import { IndustryChecklist } from "@/components/funding/IndustryChecklist";

async function download(doc) {
  const res = await api.get(`/documents/${doc.doc_id}/download`, { responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(res.data);
  a.download = doc.filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function DocRow({ d, onDelete, onExpiry, onRenew }) {
  return (
    <div className="flex items-center gap-3 py-2" data-testid={`doc-row-${d.doc_id}`}>
      <FileText className="h-4 w-4 text-slate-400 shrink-0" />
      <span className="text-sm text-slate-700 truncate flex-1">{d.filename}</span>
      {d.facts_status === "ready" && <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 whitespace-nowrap" title="Details from this file fill your proposals" data-testid={`doc-facts-ready-${d.doc_id}`}>Read for proposals</span>}
      {["queued", "running"].includes(d.facts_status) && <span className="text-[11px] text-slate-500 flex items-center gap-1 whitespace-nowrap" data-testid={`doc-facts-reading-${d.doc_id}`}><Loader2 className="h-3 w-3 animate-spin" />Reading</span>}
      {["unreadable", "unsupported"].includes(d.facts_status) && <span className="text-[11px] text-slate-400 whitespace-nowrap" title="Save as PDF, Word (.docx), Excel (.xlsx) or a photo so it can be read" data-testid={`doc-facts-unreadable-${d.doc_id}`}>Can't read</span>}
      {d.replaced_by && <span className="text-xs text-slate-400 whitespace-nowrap" data-testid={`doc-replaced-${d.doc_id}`}>Replaced {new Date(d.replaced_at).toLocaleDateString()}</span>}
      {d.expires_on && onRenew && <button onClick={() => onRenew(d)} className="text-xs text-emerald-700 font-medium whitespace-nowrap hover:underline" data-testid={`doc-renew-${d.doc_id}`}>Renewed?</button>}
      {EXPIRING.has(d.category) && d.status === "ready" && !d.replaced_by && (
        <button onClick={() => onExpiry(d)} className={`text-xs whitespace-nowrap hover:underline ${d.expires_on ? "text-slate-500" : "text-emerald-700 font-medium"}`} data-testid={`doc-expiry-${d.doc_id}`}>
          {d.expires_on ? `Expires ${new Date(`${d.expires_on}T12:00:00`).toLocaleDateString()}` : "Add expiry"}</button>
      )}
      {d.status === "pending" && <span className="text-xs text-emerald-700 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />Generating</span>}
      {d.status === "error" && <span className="text-xs text-red-600">Failed</span>}
      {d.status === "ready" && <button onClick={() => download(d).catch(() => toast.error("Download failed"))} className="p-1.5 text-slate-500 hover:text-emerald-700" data-testid={`doc-download-${d.doc_id}`}><Download className="h-4 w-4" /></button>}
      <button onClick={() => onDelete(d)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`doc-delete-${d.doc_id}`}><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}

function CategoryCard({ c, docs, onUpload, onDelete, onExpiry, onRenew }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setBusy(true);
    await onUpload(c.key, f);
    setBusy(false);
    e.target.value = "";
  };
  const has = docs.length > 0;
  return (
    <div className={`bg-white border rounded-lg p-5 ${has ? "border-emerald-200" : c.required ? "border-amber-200" : "border-slate-200"}`} data-testid={`vault-category-${c.key}`}>
      <div className="flex items-start gap-3">
        {has ? <FileCheck2 className="h-5 w-5 text-emerald-600 shrink-0" /> : <FileWarning className={`h-5 w-5 shrink-0 ${c.required ? "text-amber-500" : "text-slate-300"}`} />}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-slate-900">{c.label}</div>
          <div className="text-xs text-slate-500">{c.required ? "Required for most applications" : "Optional"}</div>
        </div>
        <input ref={ref} type="file" className="hidden" onChange={pick} accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.txt,.csv" data-testid={`vault-file-input-${c.key}`} />
        <Button size="sm" variant="outline" disabled={busy} onClick={() => ref.current.click()} data-testid={`vault-upload-${c.key}`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}</Button>
      </div>
      {has && <div className="mt-3 border-t border-slate-100 pt-1">{docs.map((d) => <DocRow key={d.doc_id} d={d} onDelete={onDelete} onExpiry={onExpiry} onRenew={onRenew} />)}</div>}
    </div>
  );
}

function Generator({ docs, onCreated }) {
  const { hasPkg } = useAuth();
  const [f, setF] = useState({ doc_type: "executive_summary", proposal_id: "none", notes: "" });
  const [props, setProps] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (hasPkg("proposal_writer")) api.get("/proposals").then((r) => setProps(r.data)); }, [hasPkg]);
  const gen = async () => {
    setBusy(true);
    try {
      await api.post("/documents/generate", { ...f, proposal_id: f.proposal_id === "none" ? null : f.proposal_id });
      toast.success("Generating your PDF — it'll appear below in about a minute");
      onCreated();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-slate-900 text-white rounded-lg p-6" data-testid="doc-generator">
      <div className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-emerald-400" /><h3 className="font-display text-xl font-bold">Generate application attachments</h3></div>
      <p className="text-sm text-slate-300 mt-1">Formatted PDFs built from your business profile — ready to attach to applications.</p>
      {!hasPkg("proposal_writer") ? (
        <div className="mt-5"><Button onClick={() => startCheckout("proposal_writer_monthly")} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950" data-testid="doc-generator-unlock-button">Unlock with Proposal Writer — $49/mo</Button><CheckoutDisclosure dark className="mt-3" id="doc-generator" /></div>
      ) : (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-4"><Label className="text-slate-300">Document</Label>
            <Select value={f.doc_type} onValueChange={(v) => setF({ ...f, doc_type: v })}>
              <SelectTrigger className="mt-1.5 bg-slate-800 border-slate-700" data-testid="doc-generator-type-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="executive_summary">Executive Summary</SelectItem><SelectItem value="capex_budget">Capital Expenditure Budget</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="md:col-span-8"><Label className="text-slate-300">Based on proposal (optional)</Label>
            <Select value={f.proposal_id} onValueChange={(v) => setF({ ...f, proposal_id: v })}>
              <SelectTrigger className="mt-1.5 bg-slate-800 border-slate-700" data-testid="doc-generator-proposal-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">Business profile only</SelectItem>{props.map((p) => <SelectItem key={p.proposal_id} value={p.proposal_id}>{p.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-12"><Textarea rows={2} placeholder="Notes, e.g. equipment you plan to buy, amounts, priorities" className="bg-slate-800 border-slate-700 text-white" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="doc-generator-notes-input" /></div>
          <div className="md:col-span-12"><Button onClick={gen} disabled={busy} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950" data-testid="doc-generator-submit-button">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Generate PDF</Button></div>
        </div>
      )}
      {docs.length > 0 && <div className="mt-6 bg-white rounded-md px-4 py-2" data-testid="generated-docs-list">{docs.map((d) => <DocRow key={d.doc_id} d={d} onDelete={() => {}} />)}</div>}
    </div>
  );
}

export default function Documents() {
  const [cats, setCats] = useState([]);
  const [docs, setDocs] = useState(null);
  const [rk, setRk] = useState(0);
  const [scan, setScan] = useState(false);
  const [expDoc, setExpDoc] = useState(null);
  const [renewDoc, setRenewDoc] = useState(null);
  const askExpiry = (doc) => EXPIRING.has(doc?.category) && setExpDoc(doc);
  const load = useCallback(() => api.get("/documents").then((r) => setDocs(r.data)), []);
  useEffect(() => { api.get("/documents/categories").then((r) => setCats(r.data)); load(); }, [load]);
  usePoll(load, !!docs?.some((d) => d.status === "pending" || ["queued", "running"].includes(d.facts_status)), 5000);
  const upload = async (category, file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    try { const r = await api.post("/documents", fd); toast.success("Uploaded"); await load(); setRk((k) => k + 1); askExpiry(r.data); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (d) => { if (!window.confirm(`Delete ${d.filename}?`)) return; await api.delete(`/documents/${d.doc_id}`); await load(); setRk((k) => k + 1); };
  if (!docs) return <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />;
  const byCat = (k) => docs.filter((d) => d.category === k);
  return (
    <div>
      <PageHeader eyebrow="Document Vault" title="Compliance documents" action={<Button onClick={() => setScan(true)} className="bg-emerald-600 hover:bg-emerald-700 h-11" data-testid="scan-open-button"><Camera className="h-4 w-4 mr-2" />Scan with camera</Button>}>Store your standard corporate attachments in one secure place, shared with your team.</PageHeader>
      <IndustryChecklist onUploaded={load} />
      <ReadinessMeter key={rk} compact />
      <div className="mt-6"><RenewalStreak reloadKey={rk} /></div>
      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
        {cats.map((c) => <CategoryCard key={c.key} c={c} docs={byCat(c.key)} onUpload={upload} onDelete={del} onExpiry={setExpDoc} onRenew={setRenewDoc} />)}
      </div>
      <div className="mt-10"><Generator docs={byCat("generated")} onCreated={load} /></div>
      <ScanDialog open={scan} onClose={() => setScan(false)} cats={cats.filter((c) => c.key !== "generated")} onSaved={(doc) => { load(); setRk((k) => k + 1); askExpiry(doc); }} />
      {expDoc && <ExpiryDialog doc={expDoc} onClose={() => setExpDoc(null)} onSaved={load} />}
      {renewDoc && <RenewDialog doc={renewDoc} onClose={() => setRenewDoc(null)} onSaved={() => { load(); setRk((k) => k + 1); }} />}
    </div>
  );
}
